/* eslint-disable camelcase */
/* eslint-disable react/prop-types */

import React, { useContext, useEffect, useRef, useState } from "react";
import { PropTypes } from "prop-types";
import { graphql, Link } from "gatsby";
import Img from "gatsby-image";
import Glide from "@glidejs/glide";
import { AppContext } from "~context/AppContext";
import { DocumentContext } from "~context/DocumentContext";
import Button from "~components/Button";
import Carousel from "~components/Carousel";
import Footer from "~components/Footer";
import GradientBorder from "~components/GradientBorder";
import Layout from "~components/Layout";
import Parallaxer from "~components/Parallaxer";
import SEO from "~components/SEO";
import { trigger } from "~utils/analytics";
import {
  getProductImage,
  getSelectableOptions,
  getVariantByOptions
} from "~utils/shopify";

const ProductPage = ({ data, location }) => {
  const { addToCart, inventoryCache, inventoryFetched } = useContext(
    AppContext
  );
  const { device } = useContext(DocumentContext);

  const imageGlideRef = useRef();
  const imageZoomRef = useRef();

  const [cartProduct, setCartProduct] = useState(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [expanded, setExpanded] = useState(`Description`);
  const [glide, setGlide] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [soldOutVariants, setSoldOutVariants] = useState([]);
  const [soldOut, setSoldOut] = useState(null);
  const [triggered, setTriggered] = useState(false);
  const [zoomTransform, setZoomTransform] = useState(
    `translate3d(-50%, -50%, 0)`
  );

  //

  const backToTop = () => {
    if (!window) {
      return;
    }

    //

    window.scrollTo({
      top: 0,
      behavior: `smooth`
    });
  };

  //
  // glide

  useEffect(() => {
    if (!device || !imageGlideRef?.current || glide) {
      return;
    }

    const glideCarousel = new Glide(`#pdp-glide`, {
      autoPlay: false,
      gap: 0,
      type: `carousel`
    });

    glideCarousel.on(`run`, () => {
      setCarouselIndex(glideCarousel.index);
    });

    setGlide(glideCarousel);
  }, [device, imageGlideRef?.current]);

  useEffect(() => {
    if (glide) {
      glide.mount();
    }
  }, [glide]);

  //

  const cms = data.sanityProduct;

  const [activeImage, setActiveImage] = useState(cms?.image?.asset?.fluid);

  let masterCollection;

  cms.collections.forEach(collection => {
    if (!masterCollection && collection.handle !== `best-sellers`) {
      masterCollection = collection;
    }
  });

  const options = getSelectableOptions(cms.shopifyProduct);

  const zoomImage = e => {
    if (!imageZoomRef?.current || device !== `desktop`) {
      return;
    }

    const boundingRect = imageZoomRef.current.getBoundingClientRect();

    let transformPercentX = parseFloat(
      ((e.pageX - boundingRect.left) / boundingRect.width) * 100
    ).toFixed(2);

    if (transformPercentX > 100) {
      transformPercentX = 100;
    } else if (transformPercentX < 0) {
      transformPercentX = 0;
    }

    let transformPercentY = parseFloat(
      ((e.pageY - boundingRect.top) / boundingRect.height) * 100
    ).toFixed(2);

    if (transformPercentY > 100) {
      transformPercentY = 100;
    } else if (transformPercentY < 0) {
      transformPercentY = 0;
    }

    setZoomTransform(
      `translate3d(-${transformPercentX}%, -${transformPercentY}%, 0) scale(2)`
    );
  };

  //

  /**
   * ---------------------------------------------------------------------------
   * useEffect []
   * Loads on page render and sets a default variant based on the first set
   * of available selectable Shopify options.
   */
  useEffect(() => {
    const defaultOptions = {};

    Object.keys(options).forEach(optionKey => {
      defaultOptions[optionKey] = options[optionKey]?.[0];
    });

    setSelectedOptions(defaultOptions);
  }, []);

  /**
   * ---------------------------------------------------------------------------
   * useEffect [selectedOptions]
   * Executes every time a user changes a selectable option. This will create
   * a cartProduct, which contains the cartProduct.variant object required for
   * checkout.
   */
  useEffect(() => {
    setCartProduct({
      ...cms.shopifyProduct,
      variant: getVariantByOptions(cms.shopifyProduct, selectedOptions),
      productType: masterCollection.title
    });
  }, [selectedOptions]);

  /**
   * ---------------------------------------------------------------------------
   * useEffect [cartProduct]
   * Executes whenever the cartProduct is set. Useful for adding analytics
   * methods or additional hooks that fire on a user selecting a product.
   */
  useEffect(() => {
    if (!cartProduct || triggered) {
      return;
    }

    setTriggered(true);
  }, [cartProduct]);

  useEffect(() => {
    if (triggered) {
      // todo : leaving FB out of here for the moment
      trigger([`ga`], `productView`, { product: cartProduct });
    }
  }, [triggered]);

  /**
   * ---------------------------------------------------------------------------
   * useEffect [inventoryCache]
   * Reads the inventoryCache for live stock availability
   */

  useEffect(() => {
    if (!inventoryCache) {
      return;
    }

    const unstockedVariants = [];

    Object.keys(inventoryCache).forEach(variantId => {
      if (
        inventoryCache[variantId] !== null &&
        inventoryCache[variantId] <= 0
      ) {
        unstockedVariants.push(variantId);
      }
    });

    setSoldOutVariants(unstockedVariants);
  }, [inventoryCache]);

  useEffect(() => {
    if (inventoryFetched) {
      setSoldOut(soldOutVariants?.includes(cartProduct?.variant?.id));
    }
  }, [soldOutVariants]);

  //

  return (
    <>
      <SEO
        customTitle={cms?.title}
        customDescription={cms?.seoDescription}
        customKeywords={cms?.seoKeywords}
        path={location.pathname}
      />

      <Layout
        className={`product-page w-full relative md:pb-12 ${
          device === `desktop` ? `pt-v8 pb-v12` : `pt-20 pb-20`
        } bg-bone`}
      >
        <section className="grid">
          {(device && device === `desktop` && (
            <div
              className={`animation-appear-down-slow animation-delay-1 grid-end-12 ${
                device === `desktop` ? `mb-v2 b2` : `mb-12 caption`
              }`}
            >
              <p className="b2">
                <Link to="/collections" className="hover-underline">
                  Shop
                </Link>

                {masterCollection?.handle && <span> . </span>}

                {masterCollection?.handle && (
                  <Link
                    to={`/collections/${masterCollection.handle}`}
                    className="hover-underline"
                  >
                    {`${masterCollection.title}`}
                  </Link>
                )}

                <span> . </span>

                {`${cms.title}`}
              </p>
            </div>
          )) || (
            <header className="grid-end-12 text-center">
              <h1 className="animation-appear-left animation-delay-2 h2 italic">
                {cms.title}
              </h1>

              {cms?.brands?.[0]?.title && (
                <h2 className="animation-appear-left animation-delay-2 h2">
                  {cms.brands[0].title}
                </h2>
              )}

              <h3 className="animation-appear-left animation-delay-3 mt-3 mb-3 b1">
                {cartProduct?.variant?.price && (
                  <span>${cartProduct.variant.price}</span>
                )}
              </h3>
            </header>
          )}

          {device && device === `desktop` && (
            <div className="animation-appear-down-slow animation-delay-2 grid-end-2 md:grid-end-1 relative">
              <button
                type="button"
                className={`${
                  activeImage === cms?.image?.asset?.fluid ? `selected` : ``
                } product-page__gallery-button square block mb-v2`}
                onClick={() => setActiveImage(cms.image.asset.fluid)}
              >
                <figure className="w-full h-full absolute top-0 right-0 bottom-0 left-0 flex items-center justify-center overflow-hidden">
                  {getProductImage(cms)}
                </figure>
              </button>

              {cms?.gallery?.map((galleryItem, galleryItemIndex) => {
                const key = `gallery-${galleryItemIndex}`;

                if (!galleryItem?.asset?.fluid) {
                  return <React.Fragment key={key}></React.Fragment>;
                }

                return (
                  <button
                    key={key}
                    type="button"
                    className={`${
                      activeImage === galleryItem.asset.fluid ? `selected` : ``
                    } product-page__gallery-button square block mb-v2`}
                    onClick={() => setActiveImage(galleryItem.asset.fluid)}
                  >
                    <figure className="w-full h-full absolute top-0 right-0 bottom-0 left-0 flex items-center justify-center overflow-hidden">
                      <Img
                        className="w-full h-full relative object-cover"
                        fluid={galleryItem.asset.fluid}
                        alt={`${cms.title} gallery ${galleryItemIndex}`}
                      />
                    </figure>
                  </button>
                );
              })}
            </div>
          )}

          {device && device === `desktop` && (
            <article className="animation-appear-right animation-delay-2 md:grid-end-5 relative">
              <div
                ref={imageZoomRef}
                className="relative overflow-hidden hidden md:block"
                style={{ cursor: `zoom-in`, paddingBottom: `130%` }}
                onMouseMove={e => zoomImage(e)}
                onMouseLeave={() =>
                  setZoomTransform(`translate3d(-50%, -50%, 0)`)
                }
              >
                <figure
                  className="w-full h-full absolute top-0 right-0 bottom-0 left-0"
                  style={{
                    maxWidth: `none`,
                    maxHeight: `none`,
                    top: `50%`,
                    left: `50%`,
                    transform: zoomTransform
                  }}
                >
                  <Img
                    className="w-full h-full relative block object-cover"
                    fluid={activeImage}
                    alt={cms.title}
                  />
                </figure>
              </div>
            </article>
          )}

          {device && device !== `desktop` && (
            <article className="animation-appear-down-slow animation-delay-3 grid-end-10 grid-start-2 relative">
              <div
                className="w-full relative"
                style={{ paddingBottom: `125%` }}
              >
                <div className="w-full h-full absolute top-0 right-0 bottom-0 left-0">
                  {cms?.gallery?.[0] && (
                    <div className="w-full absolute bottom-0 left-0 right-0 z-20 flex pl-3 pb-2">
                      {cms.gallery.map((galleryItem, galleryItemIndex) => {
                        const key = `pdp-glide-bullet-${galleryItemIndex}`;

                        return (
                          <div
                            key={key}
                            className={`w-3 h-3 relative block mr-1 rounded-full border ${
                              carouselIndex === galleryItemIndex
                                ? `bg-black`
                                : ``
                            }`}
                          ></div>
                        );
                      })}
                    </div>
                  )}

                  {(cms?.gallery?.[0] && (
                    <div
                      id="pdp-glide"
                      ref={imageGlideRef}
                      className="glide w-full h-full relative"
                    >
                      <div
                        className="glide__track w-full h-full relative"
                        data-glide-el="track"
                      >
                        <ul className="glide__slides w-full h-full relative">
                          <li className="glide__slide h-full relative flex items-center justify-center">
                            <figure className="w-full h-full relative block">
                              {getProductImage(cms)}
                            </figure>
                          </li>

                          {cms.gallery.map((image, imageIndex) => {
                            const key = `pdp-glide-item-${imageIndex}`;

                            return (
                              <li
                                key={key}
                                className="glide__slide h-full relative flex items-center justify-center"
                              >
                                <figure className="w-full h-full relative block">
                                  <Img
                                    className="w-full h-full relative block object-cover"
                                    fluid={image.asset.fluid}
                                    alt=""
                                  />
                                </figure>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  )) || (
                    <figure className="w-full h-full absolute top-0 right-0 bottom-0 left-0 flex items-center justify-center overflow-hidden">
                      {getProductImage(cms)}
                    </figure>
                  )}
                </div>
              </div>
            </article>
          )}

          <div className="grid-end-12 md:grid-end-5 relative pt-8 md:pt-0">
            <article className="relative">
              {device && device === `desktop` && (
                <header>
                  <h1 className="animation-appear-left animation-delay-2 h1 italic">
                    {cms.title}
                  </h1>

                  {cms?.brands?.[0]?.title && (
                    <h2 className="animation-appear-left animation-delay-2 h1">
                      {cms.brands[0].title}
                    </h2>
                  )}

                  <h3 className="animation-appear-left animation-delay-3 mt-v1 b1">
                    {cartProduct?.variant?.price && (
                      <span>${cartProduct.variant.price}</span>
                    )}
                  </h3>
                </header>
              )}

              <div
                className={`${
                  soldOut !== null ? `` : `opacity-0 pointer-events-none`
                } transition-opacity w-full relative flex justify-between ${
                  device === `desktop` ? `mt-v2` : `mt-0 mb-8`
                }`}
              >
                {(cartProduct?.variant?.price && (
                  <Button
                    className={`w-3/5 relative block ${
                      soldOut ? `opacity-50 pointer-events-none` : ``
                    }`}
                    color="white"
                    onClick={() => {
                      if (!soldOut) {
                        addToCart(cartProduct, quantity);
                      }
                    }}
                  >
                    <span className="block f-button">
                      {soldOut ? `Sold out` : `Add to cart`}
                    </span>
                  </Button>
                )) || (
                  <Button
                    className="w-full relative block opacity-25 pointer-events-none"
                    color="white"
                  >
                    <span className="f-button">
                      {device === `desktop` ? `Currently ` : ``}
                      Unavailable
                    </span>
                  </Button>
                )}

                <div
                  className={`${
                    cartProduct?.variant?.price
                      ? ``
                      : `opacity-0 pointer-events-none`
                  } w-2/5 relative flex items-center justify-center ml-v4 border bg-white`}
                >
                  <button
                    type="button"
                    className={`w-1/3 relative block ${
                      quantity <= 1 ? `pointer-events-none opacity-50` : ``
                    }`}
                    onClick={() => {
                      if (quantity < 2) {
                        return;
                      }

                      setQuantity(quantity - 1);
                    }}
                    transparent
                  >
                    <span className="block b1">—</span>
                  </button>

                  <div className="w-1/3 relative block pointer-events-none text-center">
                    <span className="block b1">{quantity}</span>
                  </div>

                  <button
                    type="button"
                    className="w-1/3 relative block"
                    onClick={() => setQuantity(quantity + 1)}
                    transparent
                  >
                    <span className="block b1">+</span>
                  </button>
                </div>
              </div>

              <ul className="animation-appear-left animation-delay-4">
                <li className="w-full relative mt-v2 border-t-grey">
                  <button
                    type="button"
                    className={`w-full md:h-auto relative block ${
                      device === `desktop` ? `pt-v1 pb-v1` : `pt-3 pb-3`
                    } text-left`}
                    onClick={() =>
                      setExpanded(
                        expanded === `Description` ? null : `Description`
                      )
                    }
                  >
                    <h3 className="f4">Description</h3>
                  </button>

                  {expanded === `Description` && (
                    <p
                      className={`animation-appear-down-slow ${
                        device === `desktop` ? `mt-v1 mb-v2` : `mt-3 mb-4`
                      } b1 whitespace-pre-wrap`}
                    >
                      {cms.description}
                    </p>
                  )}
                </li>

                {/* // filters // */}

                {cms?.hairNeeds?.[0] && (
                  <li className="w-full relative border-t-grey">
                    <button
                      type="button"
                      className={`w-full md:h-auto relative block ${
                        device === `desktop` ? `pt-v1 pb-v1` : `pt-3 pb-3`
                      } text-left`}
                      onClick={() =>
                        setExpanded(
                          expanded === `hairNeeds` ? null : `hairNeeds`
                        )
                      }
                    >
                      <h3 className="f4">Hair Needs</h3>
                    </button>

                    {expanded === `hairNeeds` && (
                      <ul
                        className={`relative flex flex-wrap items-start ${
                          device === `desktop` ? `mb-v2` : `mb-4`
                        }`}
                      >
                        {cms.hairNeeds.map((hairNeed, hairNeedIndex) => (
                          <li
                            key={`hair-need-${hairNeed}`}
                            className="animation-appear-down-slow whitespace-pre-wrap b1"
                          >
                            {hairNeed}
                            {hairNeedIndex < cms.hairNeeds.length - 1
                              ? `, `
                              : ``}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                )}
                {cms?.hairTypes?.[0] && (
                  <li className="w-full relative border-t-grey">
                    <button
                      type="button"
                      className={`w-full md:h-auto relative block ${
                        device === `desktop` ? `pt-v1 pb-v1` : `pt-3 pb-3`
                      } text-left`}
                      onClick={() =>
                        setExpanded(
                          expanded === `hairTypes` ? null : `hairTypes`
                        )
                      }
                    >
                      <h3 className="f4">Hair Types</h3>
                    </button>

                    {expanded === `hairTypes` && (
                      <ul
                        className={`relative flex flex-wrap items-start ${
                          device === `desktop` ? `mb-v2` : `mb-4`
                        }`}
                      >
                        {cms.hairTypes.map((hairType, hairTypeIndex) => (
                          <li
                            key={`hair-type-${hairType}`}
                            className="animation-appear-down-slow whitespace-pre-wrap b1"
                          >
                            {hairType}
                            {hairTypeIndex < cms.hairTypes.length - 1
                              ? `, `
                              : ``}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                )}
                {cms?.productTypes?.[0] && (
                  <li className="w-full relative border-t-grey">
                    <button
                      type="button"
                      className={`w-full md:h-auto relative block ${
                        device === `desktop` ? `pt-v1 pb-v1` : `pt-3 pb-3`
                      } text-left`}
                      onClick={() =>
                        setExpanded(
                          expanded === `productTypes` ? null : `productTypes`
                        )
                      }
                    >
                      <h3 className="f4">Product Types</h3>
                    </button>

                    {expanded === `productTypes` && (
                      <ul
                        className={`relative flex flex-wrap items-start ${
                          device === `desktop` ? `mb-v2` : `mb-4`
                        }`}
                      >
                        {cms.productTypes.map(
                          (productType, productTypeIndex) => (
                            <li
                              key={`product-type-${productType}`}
                              className="animation-appear-down-slow whitespace-pre-wrap b1"
                            >
                              {productType}
                              {productTypeIndex < cms.productTypes.length - 1
                                ? `, `
                                : ``}
                            </li>
                          )
                        )}
                      </ul>
                    )}
                  </li>
                )}
                {cms?.features?.[0] && (
                  <li className="w-full relative border-t-grey">
                    <button
                      type="button"
                      className={`w-full md:h-auto relative block ${
                        device === `desktop` ? `pt-v1 pb-v1` : `pt-3 pb-3`
                      } text-left`}
                      onClick={() =>
                        setExpanded(expanded === `features` ? null : `features`)
                      }
                    >
                      <h3 className="f4">Features</h3>
                    </button>

                    {expanded === `features` && (
                      <ul
                        className={`relative flex flex-wrap items-start ${
                          device === `desktop` ? `mb-v2` : `mb-4`
                        }`}
                      >
                        {cms.features.map((feature, featureIndex) => (
                          <li
                            key={`feature-${feature}`}
                            className="animation-appear-down-slow whitespace-pre-wrap b1"
                          >
                            {feature}
                            {featureIndex < cms.features.length - 1 ? `, ` : ``}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                )}

                {/* // attributes // */}

                {cms?.attributes?.[0] &&
                  cms.attributes.map(({ title, content }) => {
                    return (
                      <li key={title} className="w-full relative border-t-grey">
                        <button
                          type="button"
                          className={`w-full md:h-auto relative block ${
                            device === `desktop` ? `pt-v1 pb-v1` : `pt-3 pb-3`
                          } text-left`}
                          onClick={() =>
                            setExpanded(expanded === title ? null : title)
                          }
                        >
                          <h3 className="f4">{title}</h3>
                        </button>

                        {expanded === title && (
                          <p
                            className={`animation-appear-down-slow ${
                              device === `desktop` ? `mt-v1 mb-v2` : `mt-3 mb-4`
                            } whitespace-pre-wrap b1`}
                          >
                            {content}
                          </p>
                        )}
                      </li>
                    );
                  })}
              </ul>
            </article>
          </div>
        </section>

        {/* // */}

        <section
          className={`w-full relative block ${
            device === `desktop` ? `pb-v6` : `pb-24`
          }`}
        >
          {cms?.sections?.[0] &&
            cms.sections.map((section, sectionIndex) => {
              const sectionKey = `pdp-section-${sectionIndex}`;

              let sectionJSX = <></>;

              // eslint-disable-next-line no-underscore-dangle
              switch (section?.__typename) {
                case `SanityImageSection`:
                  sectionJSX = (
                    <section key={sectionKey} className="grid">
                      <figure className="grid-end-12 relative mt-6 mb-6">
                        <Img
                          className="w-full relative block"
                          fluid={section.image.asset.fluid}
                          alt={section.image.altText}
                        />
                      </figure>
                    </section>
                  );

                  break;

                case `SanitySimpleTextSection`:
                  sectionJSX = (
                    <section key={sectionKey} className="relative mt-24 mb-24">
                      <article className="grid">
                        <h2 className="grid-end-12 f2 text-center">
                          {section.heading}
                        </h2>

                        <p className="grid-end-12 md:grid-end-8 md:grid-start-3 mt-8 whitespace-pre-wrap b1 text-center">
                          {section.content}
                        </p>
                      </article>
                    </section>
                  );

                  break;

                case `SanityVideoSection`:
                  sectionJSX = (
                    <section key={sectionKey} className="grid">
                      <div className="grid-end-12 relative mt-6 mb-6">
                        <video
                          className="w-full relative block"
                          autoPlay
                          loop
                          muted
                          playsinline
                        >
                          <source src={section.videoFile.asset.url}></source>
                        </video>
                      </div>
                    </section>
                  );

                  break;

                default:
                  break;
              }

              return sectionJSX;
            })}
        </section>

        {/* // */}

        <section
          className={`w-full relative block ${
            cms?.quote || cms?.imageLeft || cms?.imageRight ? `pb-v6` : ``
          }`}
        >
          <div className="grid">
            {cms?.quote && (
              <h2
                className={`grid-end-6 md:grid-end-4 md:grid-start-4 whitespace-pre-wrap ${
                  device === `desktop` ? `mt-v8 h1` : `mt-24 pr-4 b1`
                } italic`}
              >
                {cms.quote}
              </h2>
            )}

            <figure className="grid-end-6 md:grid-end-3">
              {cms?.imageLeft?.asset?.fluid && (
                <Parallaxer className="pl-2 md:pl-0" severe>
                  <Img
                    className="w-full relative block"
                    fluid={cms.imageLeft.asset.fluid}
                    alt={cms.imageLeft.imageAlt || cms.title}
                  />
                </Parallaxer>
              )}
            </figure>

            <div className="product-page__situ grid-end-12 md:grid-end-9 md:grid-start-3 pr-v1 flex items-end justify-between">
              {cms?.imageRight?.asset?.fluid && (
                <Parallaxer className="w-1/2 mb-12 md:mb-0 pr-2 md:pr-0">
                  <Img
                    className="w-full relative block"
                    fluid={cms.imageRight.asset.fluid}
                    alt={cms.imageRight.imageAlt || cms.title}
                  />
                </Parallaxer>
              )}

              {cms?.joeyKnows && (
                <div className="w-1/2 md:w-1/3 relative block pl-4 md:pl-0 mb-12 md:mb-0">
                  <h3
                    className={`${
                      device === `desktop` ? `pt-v05 pr-v05 pb-v05 pl-v05` : ``
                    } f4 bg-orange`}
                  >
                    Joey knows
                  </h3>

                  <p className={`${device === `desktop` ? `mt-v1` : ``} b2`}>
                    {cms.joeyKnows}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="w-full relative flex justify-center md:justify-end mt-24 md:pr-8">
            <Button onClick={backToTop} type="button">
              <span className="f-button">Return to top</span>
            </Button>
          </div>

          <GradientBorder />
        </section>

        {cms?.related?.[0] && (
          <section
            className={`grid ${device === `desktop` ? `pt-v3` : `pt-8`}`}
          >
            <header className="grid-end-12 md:grid-end-2 mb-8 md:mb-0 text-center md:text-left">
              <h3 className="animation-appear-down-slow animation-delay-2 f3">
                You might love
              </h3>
            </header>

            {cms.related.map((product, productIndex) => {
              const key = `${product.handle}-${productIndex}`;
              const relatedSoldOut = soldOutVariants?.includes(
                product?.shopifyProduct?.variants?.[0]?.id
              );

              //

              if (productIndex > 2) {
                return <React.Fragment key={key}></React.Fragment>;
              }

              return (
                <article
                  key={key}
                  className={`product-grid__item grid-end-6 md:grid-end-3 relative flex flex-col items-stretch ${
                    device === `desktop` ? `mb-v4` : `mb-12`
                  }`}
                >
                  <Link
                    to={`/products/${product.handle}`}
                    className="product-grid__item__image relative overflow-hidden bg-light-bone"
                  >
                    <figure className="w-full h-full absolute top-0 right-0 bottom-0 left-0 flex items-center justify-center">
                      {getProductImage(product)}
                    </figure>
                  </Link>

                  <Link
                    to={`/products/${product.handle}`}
                    className="product-grid__item__text relative hover-underline"
                  >
                    <h2
                      className={`hover-underline ${
                        device === `desktop` ? `mt-v1` : `mt-4`
                      } b2 italic`}
                    >
                      {product.title}
                    </h2>

                    {product?.brands?.[0]?.title && (
                      <h3 className="b2">{product.brands[0].title}</h3>
                    )}

                    {product?.shopifyProduct?.variants?.[0]?.price && (
                      <h3
                        className={`${device === `desktop` ? `` : `mb-3`} b2`}
                      >
                        ${product.shopifyProduct.variants[0].price}
                      </h3>
                    )}
                  </Link>

                  <div className="w-full relative flex md:block mt-v1">
                    {device !== `desktop` && (
                      <Link
                        to={`/products/${product.handle}`}
                        className={`${
                          product?.shopifyProduct?.variants?.[0]
                            ? `w-1/2`
                            : `w-full`
                        } relative block mr-1`}
                      >
                        <Button className="w-full relative block" color="white">
                          <span className="block caption">View</span>
                        </Button>
                      </Link>
                    )}

                    {(product?.shopifyProduct?.variants?.[0] && (
                      <Button
                        className={`w-1/2 md:w-full relative block ${
                          relatedSoldOut ? `opacity-25 pointer-events-none` : ``
                        } ml-1 md:ml-0`}
                        color={`${device === `desktop` ? `white` : `orange`}`}
                        onClick={() => {
                          if (!relatedSoldOut) {
                            addToCart({
                              ...product,
                              variant: product.shopifyProduct.variants[0]
                            });
                          }
                        }}
                      >
                        {(device === `desktop` && (
                          <span className="block f-button">
                            {relatedSoldOut ? `Sold out` : `Add to cart`}
                          </span>
                        )) || (
                          <span className="block caption">
                            {relatedSoldOut ? `Sold out` : `Add`}
                          </span>
                        )}
                      </Button>
                    )) || (
                      <Button
                        className="w-full relative block opacity-25 pointer-events-none"
                        color="white"
                      >
                        <span className="f-button">
                          {device === `desktop` ? `Currently ` : ``}
                          Unavailable
                        </span>
                      </Button>
                    )}
                  </div>
                </article>
              );
            })}
          </section>
        )}
      </Layout>

      <Footer />
    </>
  );
};

ProductPage.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.shape({
      frontmatter: PropTypes.shape({})
    })
  }).isRequired,
  location: PropTypes.shape({}).isRequired
};

export default ProductPage;

export const query = graphql`
  query ProductPage($id: String!) {
    sanityProduct(id: { eq: $id }) {
      title
      seoDescription
      seoKeywords

      handle
      priority
      description
      image {
        asset {
          fluid {
            ...GatsbySanityImageFluid_noBase64
          }
        }
        altText
      }
      gallery {
        asset {
          fluid {
            ...GatsbySanityImageFluid_noBase64
          }
        }
        altText
      }
      attributes {
        title
        content
      }

      hairNeeds
      hairTypes
      productTypes
      features

      quote
      imageLeft {
        asset {
          fluid {
            ...GatsbySanityImageFluid_noBase64
          }
        }
        altText
      }
      imageRight {
        asset {
          fluid {
            ...GatsbySanityImageFluid_noBase64
          }
        }
        altText
      }
      joeyKnows

      brands {
        title
      }

      collections {
        title
        handle
      }

      sections {
        ... on SanityImageSection {
          backgroundColor
          image {
            asset {
              fluid {
                ...GatsbySanityImageFluid_noBase64
              }
            }
            altText
          }
          imageWidth
        }

        ... on SanitySimpleTextSection {
          heading
          content
          withBorder
        }

        ... on SanityVideoSection {
          videoFile {
            asset {
              url
            }
          }
        }
      }

      shopifyProduct {
        id
        handle
        title
        description
        images {
          originalSrc
        }
        productType
        vendor
        variants {
          id
          sku
          title
          image {
            originalSrc
          }
          price
          selectedOptions {
            name
            value
          }
        }
      }

      related {
        title
        handle
        image {
          asset {
            fluid {
              ...GatsbySanityImageFluid_noBase64
            }
          }
          altText
        }

        brands {
          title
        }

        shopifyProduct {
          id
          handle
          title
          description
          images {
            originalSrc
          }
          productType
          vendor
          variants {
            id
            sku
            title
            image {
              originalSrc
            }
            price
            selectedOptions {
              name
              value
            }
          }
        }
      }
    }
  }
`;
